import { CircleAlertIcon as IconAlertCircle } from '@persona-id/design-tokens/icons';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import styled from 'styled-components';

import CasesSummary from 'dashboard/components/CasesSummary';
import ConnectedEventTimeline from 'dashboard/components/ConnectedEventTimeline';
import ConnectedInquiryRunTimelineCard from 'dashboard/components/ConnectedInquiryRunTimelineCard';
import InactiveEnvironmentBanner from 'dashboard/components/InactiveEnvironmentBanner';
import InquiryDetails from 'dashboard/components/InquiryDetails';
import InquiryHeader from 'dashboard/components/InquiryDetails/InquiryHeader';
import InquiryInfo from 'dashboard/components/InquiryDetails/InquiryInfo';
import ListMatches from 'dashboard/components/ListMatches';
import ModalWrapper from 'dashboard/components/ModalWrapper';
import PersonaErrorBoundary from 'dashboard/components/PersonaErrorBoundary';
import { RoutedTabbedSection } from 'dashboard/components/TabbedSectionV2';
import TagsSummary from 'dashboard/components/TagsSummary';
import Banner from 'dashboard/components-lib/Banner';
import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import NullState from 'dashboard/components-lib/NullState';
import { FlagIssuesFormData } from 'dashboard/forms/FormFlagIssues';
import { ResumeInquiryFormData } from 'dashboard/forms/FormResumeInquiry';
import { SendInquiryEmailFormData } from 'dashboard/forms/FormSendInquiryEmail';
import { FormUpdateTagsData } from 'dashboard/forms/FormUpdateTags';
import {
  Backdrop,
  ColumnContainer,
  HeaderContainer,
  MainColumn,
  RightColumn,
} from 'dashboard/layouts/ShowViewLayoutV2';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { productFeatureDisabled } from 'dashboard/lib/productFeature';
import { userDisplayName } from 'dashboard/lib/valueDisplayers/userDisplayName';
import { Case } from 'dashboard/models/case';
import { Device } from 'dashboard/models/device';
import { Document } from 'dashboard/models/document';
import { Environment } from 'dashboard/models/environment';
import { Inquiry, InquiryReview } from 'dashboard/models/inquiry';
import { InquirySession } from 'dashboard/models/inquiry-session';
import { InquiryTemplate } from 'dashboard/models/inquiry-template';
import { InquiryShowViewInquiryTemplateVersion } from 'dashboard/models/inquiry-template-version';
import { List } from 'dashboard/models/list';
import { ListItem } from 'dashboard/models/list-item';
import { ListMatch } from 'dashboard/models/list-match';
import { Organization, ProductFeatureName } from 'dashboard/models/organization';
import { Report } from 'dashboard/models/report';
import { Selfie } from 'dashboard/models/selfie';
import { Tag } from 'dashboard/models/tag';
import { Template } from 'dashboard/models/template';
import { User } from 'dashboard/models/user';
import { Verification } from 'dashboard/models/verification';
import { InquiryState } from 'dashboard/redux/reducers/inquiry';
import InquiryDeviceSessionDashboard from 'dashboard/views/DashboardInquiriesShowView/InquiryDeviceSessionDashboard';
import InquiryIntegrationLog from 'dashboard/views/DashboardInquiriesShowView/InquiryIntegrationLog';
import InquiryReports from 'dashboard/views/DashboardInquiriesShowView/InquiryReports';
import InquiryRunModal from 'dashboard/views/DashboardInquiriesShowView/InquiryRunModal';
import InquiryVerifications from 'dashboard/views/DashboardInquiriesShowView/InquiryVerifications';
import Badge from 'design-system/components/Badge';
import Card from 'design-system/components/Card';
import { DATE_AND_TIME_FORMAT, TIMEZONE_NAME_ABBREVIATED } from 'lib/constants/luxonFormats';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';
import useIsPersonerd from 'lib/hooks/useIsPersonerd';
import usePermission from 'lib/hooks/usePermission';
import useToggle from 'lib/hooks/useToggle';
import { ApiModel } from 'lib/interfaces/api';
import { FeatureFlagsMap } from 'lib/interfaces/feature-flags';
import { Permission } from 'lib/permissions';
import { captureMessage } from 'lib/reporting';

const Section = styled('div')`
  min-height: 480px;
  width: 100%;
`;

const CARD_HEIGHT = 468;
const EventTimelineCard = styled(Card)`
  height: ${CARD_HEIGHT}px;
  overflow-y: auto;
`;

interface DashboardInquiriesShowViewProps {
  creator: InquiryState['creator'];
  cases: Case[];
  devices: Device[];
  documents: Document[];
  featureFlags: FeatureFlagsMap;
  inquiry: Inquiry;
  inquiryTags: Tag[];
  inquiryTemplateVersion: InquiryShowViewInquiryTemplateVersion | undefined;
  lists: Record<string, List>;
  listItems: Record<string, ListItem>;
  listMatches: Record<string, ListMatch>;
  isLoadingInquiry: boolean;
  redactor?: User;
  reviewer: InquiryState['reviewer'];
  reviewerWorkflow: InquiryState['reviewerWorkflow'];
  reviewerWorkflowRun: InquiryState['reviewerWorkflowRun'];
  reviewerWorkflowVersion: InquiryState['reviewerWorkflowVersion'];
  reports: Report[];
  selfies: Selfie[];
  sessions: Record<string, InquirySession>;
  template: InquiryTemplate | Template;
  verifications: Verification[];
  users: User[];
  resumeCode: string;
  currentEnvironment: Environment;
  matchUrl: string;
  organization: Organization;
  nullStateMessage: string;
  currentUser: User;
  redactInquiry: () => Promise<void>;
  setResumeCode: (resumeCode: string) => void;
  handleSubmitReview: (review: InquiryReview) => Promise<void>;
  handlePrintInquiry: () => Promise<void>;
  handleFlagIssues: (values: FlagIssuesFormData) => Promise<void>;
  resumeInquiry: (values: ResumeInquiryFormData) => Promise<void>;
  sendInquiryEmail: (values: SendInquiryEmailFormData) => Promise<void>;
  handleUpdateTags: (tags: FormUpdateTagsData) => void;
  handleFlagVerificationIssues: (
    verification: Verification,
    values: FlagIssuesFormData,
  ) => Promise<void>;
  showMessage: (message: string, messageType?: MessageType, persist?: boolean) => void;
  hideNavBack?: boolean;
}

const DashboardInquiriesShowView = ({
  creator,
  cases,
  devices,
  documents,
  inquiry,
  inquiryTags,
  inquiryTemplateVersion,
  lists,
  listItems,
  listMatches,
  redactor,
  reviewer,
  currentEnvironment,
  matchUrl,
  reviewerWorkflow,
  reviewerWorkflowRun,
  reviewerWorkflowVersion,
  reports,
  selfies,
  organization,
  sessions,
  template,
  verifications,
  users,
  featureFlags,
  resumeCode,
  isLoadingInquiry,
  redactInquiry,
  setResumeCode,
  handleSubmitReview,
  handlePrintInquiry,
  handleFlagIssues,
  resumeInquiry,
  sendInquiryEmail,
  handleUpdateTags,
  handleFlagVerificationIssues,
  nullStateMessage,
  currentUser,
  hideNavBack = false,
}: DashboardInquiriesShowViewProps) => {
  const isPersonerd = useIsPersonerd();
  const fullAccessEnabled = useFeatureFlag('dashboard:ite-enable-full-access-beta');
  const showIntegrationLogsTab = !productFeatureDisabled(
    organization,
    ProductFeatureName.WorkflowsEnhanced,
    currentEnvironment?.type,
  );
  const hasVerificationView = usePermission(Permission.VerificationView);
  const [displayInquiryRunModal, { enable: showInquiryRunModal, disable: hideInquiryRunModal }] =
    useToggle(false);
  const [onlyDisplayStartedSessions, { toggle: toggleOnlyDisplayStartedSessions }] =
    useToggle(true);

  const sessionsToDisplay = useMemo(
    () =>
      onlyDisplayStartedSessions
        ? Object.values(sessions).filter((session) => session.attributes['started-at'] != null)
        : Object.values(sessions),
    [sessions, onlyDisplayStartedSessions],
  );

  if (inquiry == null) {
    return isLoadingInquiry ? (
      <Backdrop>
        <LoadingIndicator fullscreen />
      </Backdrop>
    ) : (
      <Backdrop data-test='inquiries-show-view'>
        <NullState message={nullStateMessage} backRoute='/dashboard/inquiries' />
      </Backdrop>
    );
  }

  if (template == null) {
    return null;
  }

  const redactorName = redactor == null ? 'via API' : `by ${userDisplayName(redactor)}`;
  let isRedacted = false;
  let redactionBannerText = null;
  if (inquiry.attributes['redacted-at']) {
    isRedacted = true;
    if (inquiry.attributes['marked-for-cascading-redaction-at']) {
      redactionBannerText = `This inquiry was marked for redaction ${redactorName} on ${DateTime.fromISO(
        inquiry.attributes['marked-for-cascading-redaction-at'],
      ).toFormat(DATE_AND_TIME_FORMAT)}.`;
    } else {
      redactionBannerText = `This inquiry was redacted ${redactorName} on ${DateTime.fromISO(
        inquiry.attributes['redacted-at'],
      ).toFormat(DATE_AND_TIME_FORMAT)}.`;
    }
  } else if (inquiry.attributes['parent-marked-for-cascading-redaction-at']) {
    redactionBannerText = `This inquiry was marked for redaction on ${DateTime.fromISO(
      inquiry.attributes['parent-marked-for-cascading-redaction-at'],
    ).toFormat(DATE_AND_TIME_FORMAT)}.`;
  }

  const inquiryTemplateVersionId = inquiry.relationships['inquiry-template-version']?.data?.id;
  // newly created inquiries often have no sessions or a session with null 'started-at'
  const currentLocale = sessionsToDisplay[sessionsToDisplay.length - 1]?.attributes?.locale;

  return (
    <>
      <Helmet title={`${inquiry.id} - Inquiries`} />
      <InactiveEnvironmentBanner />
      <ModalWrapper
        isOpen={displayInquiryRunModal}
        shouldCloseOnEsc={false}
        handleCloseModal={hideInquiryRunModal}
      >
        <InquiryRunModal
          currentEnvironment={currentEnvironment}
          inquiry={inquiry}
          locale={currentLocale}
          handleClose={hideInquiryRunModal}
        />
      </ModalWrapper>
      {redactionBannerText && <Banner icon={<IconAlertCircle />}>{redactionBannerText}</Banner>}
      <Backdrop data-test='inquiries-show-view'>
        <Section data-test='inquiries-show-view__inquiry-info'>
          <HeaderContainer>
            <InquiryHeader
              inquiry={inquiry}
              verifications={verifications}
              documents={documents}
              resumeCode={resumeCode}
              redactInquiry={redactInquiry}
              setResumeCode={setResumeCode}
              handleSubmitReview={handleSubmitReview}
              handlePrintInquiry={handlePrintInquiry}
              handleFlagIssues={handleFlagIssues}
              resumeInquiry={resumeInquiry}
              sendInquiryEmail={sendInquiryEmail}
              currentEnvironment={currentEnvironment}
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              subdomain={organization.attributes.subdomain}
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              customDomain={organization.attributes['inquiry-hosted-flow-custom-domain']}
              hideNavBack={hideNavBack}
              currentUser={currentUser}
            />
          </HeaderContainer>
          <ColumnContainer>
            <MainColumn>
              <RoutedTabbedSection
                dataTest='inquiries-show-view__inquiry-tabs'
                tabs={[
                  {
                    name: 'Overview',
                    path: '/',
                    dataTest: 'inquiries-show-view__inquiry-tabs--overview',
                    contents: (
                      <InquiryDetails
                        featureFlags={featureFlags}
                        documents={documents}
                        inquiry={inquiry}
                        inquiryTemplateVersion={inquiryTemplateVersion}
                        isRedacted={isRedacted}
                        listMatches={listMatches}
                        reports={reports}
                        selfies={selfies}
                        sessions={sessions}
                        template={template}
                        verifications={verifications}
                        matchUrl={matchUrl}
                        environmentType={currentEnvironment.type}
                      />
                    ),
                  },
                  hasVerificationView && {
                    name: 'Verifications',
                    badge: <Badge>{verifications.length}</Badge>,
                    path: '/verifications',
                    dataTest: 'inquiries-show-view__inquiry-tabs--verifications',
                    contents: (
                      <InquiryVerifications
                        inquiry={inquiry}
                        verifications={verifications}
                        documents={documents}
                        featureFlags={featureFlags}
                        environmentType={currentEnvironment.type}
                        handleFlagVerificationIssues={handleFlagVerificationIssues}
                      />
                    ),
                  },
                  {
                    name: 'Sessions',
                    badge: <Badge>{sessionsToDisplay.length}</Badge>,
                    path: '/sessions',
                    dataTest: 'inquiries-show-view__inquiry-tabs--sessions',
                    contents: (
                      <InquiryDeviceSessionDashboard
                        inquirySessions={sessionsToDisplay}
                        devices={devices}
                        isRedacted={isRedacted}
                        onlyDisplayStartedSessions={onlyDisplayStartedSessions}
                        toggleOnlyDisplayStartedSessions={toggleOnlyDisplayStartedSessions}
                      />
                    ),
                  },
                  {
                    name: 'Reports',
                    badge: <Badge>{reports.length}</Badge>,
                    path: '/reports',
                    exact: false,
                    dataTest: 'inquiries-show-view__inquiry-tabs--reports',
                    contents: <InquiryReports isLoading={isLoadingInquiry} reports={reports} />,
                  },
                  {
                    name: 'List matches',
                    badge: <Badge>{Object.keys(listMatches).length}</Badge>,
                    path: '/lists',
                    dataTest: 'inquiries-show-view__inquiry-tabs--lists',
                    contents: (
                      <ListMatches lists={lists} listItems={listItems} listMatches={listMatches} />
                    ),
                  },
                  showIntegrationLogsTab && {
                    name: 'Integration Logs',
                    path: '/integration-logs',
                    dataTest: 'inquiries-show-view__inquiry-tabs--integration-logs',
                    contents: <InquiryIntegrationLog inquiryId={inquiry.id} />,
                  },
                ]}
              />
            </MainColumn>
            <RightColumn>
              <InquiryInfo
                template={template}
                inquiry={inquiry}
                reviewer={reviewer}
                reviewerWorkflow={reviewerWorkflow}
                reviewerWorkflowRun={reviewerWorkflowRun}
                reviewerWorkflowVersion={reviewerWorkflowVersion}
                creator={creator}
              />
              <TagsSummary
                tags={inquiryTags}
                tagScope={ApiModel.Inquiry}
                handleUpdateTags={handleUpdateTags}
              />
              <CasesSummary
                cases={cases}
                users={users}
                nullStateText='There are no cases associated with this inquiry.'
              />
              <PersonaErrorBoundary
                onCatch={(error) =>
                  captureMessage('Event timeline failed to load', 'error', {
                    error,
                    object: inquiry.id,
                    component: 'DashboardInquiriesShowView',
                  })
                }
              >
                {inquiryTemplateVersionId != null ? (
                  <ConnectedInquiryRunTimelineCard
                    inquiry={inquiry}
                    currentEnvironment={currentEnvironment}
                    locale={currentLocale}
                    modalButtonEnabled={isPersonerd || fullAccessEnabled}
                    handleShowRunsModal={showInquiryRunModal}
                  />
                ) : (
                  <EventTimelineCard>
                    <Card.Body>
                      <Card.Title>
                        Event timeline ({DateTime.local().toFormat(TIMEZONE_NAME_ABBREVIATED)})
                      </Card.Title>
                      <ConnectedEventTimeline
                        objects={[inquiry, ...verifications]}
                        location='inquiry'
                        currentEnvironment={currentEnvironment}
                      />
                    </Card.Body>
                  </EventTimelineCard>
                )}
              </PersonaErrorBoundary>
            </RightColumn>
          </ColumnContainer>
        </Section>
      </Backdrop>
    </>
  );
};

export default DashboardInquiriesShowView;
